import React, { useEffect, useState } from 'react'
import QuoteView from '../../../components/QuoteView/QuoteView'
import { useFormContext } from 'react-hook-form'
import { useHistory } from 'react-router-dom'
import { IQuoteFormData } from '../QuoteForm'
import {
  Container,
  Spinner,
  useToaster
} from 'react-angle-dashboard-components'
import FooterNav from '../FooterNav'
import {
  usePastMedicalPlans,
  usePollForCensusRiskAssessment,
  useQuote,
  useTriggerQuoteRateCalculation
} from '../../../hooks/quote.hooks'
import { mapQuoteDtoToQuoteView } from '../../../services/quote/mappers'
import { Paths } from '../../../routes/paths'
import { isSchemaValid } from '../helper'
import {
  basicInfoSchema,
  censusInfoSchema,
  planInfoSchema,
  quoteDetailsSchema
} from '../schemas'

const QuoteReview = () => {
  const history = useHistory()
  const { showError } = useToaster()
  const { watch } = useFormContext<IQuoteFormData>()

  const [isPollingForRates, setIsPollingForRates] = useState(false)
  const [shouldAdvanceToNextScreen, setShouldAdvanceToNextScreen] =
    useState(false)

  const formData = watch()

  const quoteId = formData.entryId?.toString()

  const { data: quoteApiValues, isLoading: currentQuoteDataLoading } =
    useQuote(quoteId)

  const { data: pastMedPlans, isLoading: currentPastMedPlansLoading } =
    usePastMedicalPlans(quoteId)

  const { data: risk } = usePollForCensusRiskAssessment(quoteId ?? null)

  useEffect(() => {
    if (quoteId && risk) {
      if (!isSchemaValid(watch('basicInfo'), basicInfoSchema)) {
        history.replace(Paths.basicInfoStep1(quoteId))
      } else if (!isSchemaValid(watch('planInfo'), planInfoSchema)) {
        history.replace(Paths.quoteFormPlanInfo(quoteId))
      } else if (!isSchemaValid(watch('censusInfo'), censusInfoSchema)) {
        history.replace(Paths.quoteFormCensusInfo(quoteId))
      } else if (risk?.result === 'errored') {
        history.replace(Paths.quoteFormCensusInfo(quoteId))
      } else if (!isSchemaValid(watch('quoteDetails'), quoteDetailsSchema)) {
        history.replace(Paths.quoteFormQuoteDetailsStep1(quoteId))
      }
    }
  }, [history, quoteId, watch, risk])

  const { data: rateStatus, isLoading: isRateStatusLoading } =
    useTriggerQuoteRateCalculation(quoteId, isPollingForRates)

  useEffect(() => {
    if (shouldAdvanceToNextScreen && rateStatus && quoteId) {
      history.replace(Paths.viewQuote(quoteId))
    }
  }, [shouldAdvanceToNextScreen, history, quoteId, rateStatus])

  if (
    currentQuoteDataLoading ||
    currentPastMedPlansLoading ||
    !quoteApiValues ||
    !pastMedPlans
  ) {
    return <Spinner animation="border" />
  }

  const quoteViewData = mapQuoteDtoToQuoteView(
    quoteApiValues,
    quoteApiValues.prevFundingType === null || pastMedPlans.length > 0
  )

  const onSubmit = async () => {
    try {
      if (quoteId) {
        setIsPollingForRates(true)
        setShouldAdvanceToNextScreen(true)
      }
    } catch (err) {
      console.error(err)
      showError(
        'There was a problem submitting your quote. Please try again or contact support.'
      )
    }
  }

  return (
    <div>
      <Container>
        <QuoteView quote={quoteViewData} quoteStatus={quoteApiValues.status} overrideShowRates={true}/>
        <FooterNav
          triggerPath={undefined}
          continueButtonText="Submit"
          onContinue={onSubmit}
          isLoading={isRateStatusLoading}
        />
      </Container>
    </div>
  )
}

export default QuoteReview
